import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { FormGroup, Label, Col, Button, Row, Input } from 'reactstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';
import Header from '../Components/Header';
import { BASE_URL } from '../config';

function EditDoctorSchedule() {
    const { id } = useParams(); // This is the doctor schedule ID
    const navigate = useNavigate(); // To navigate after successful edit

    // Initialize form data
    const [formData, setFormData] = useState({
        startTime: '',
        endTime: '',
        dayofweek: '',
        status: 'Active'
    });
    const [doctorId, setDoctorId] = useState(''); // Store doctorId
    const [scheduleData, setScheduleData] = useState([]); // Store current schedule data
    const [conflictingTimes, setConflictingTimes] = useState([]); // Store conflicting time ranges
    const [loading, setLoading] = useState(false); // Track loading state

    const daysOfWeek = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
    ];

    const minutesOptions = ['00', '20', '40']; // Custom minute options
    const hoursOptions = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2, '0')); // 24-hour format hours

    // Fetch existing schedule details when the component is mounted
    useEffect(() => {
        const fetchScheduleData = async () => {
            try {
                const response = await fetch(`${BASE_URL}SpecialistDoctorScheduleId?Sid=${id}`, {
                    method: 'GET',
                    headers: { 'accept': 'text/plain' }
                });

                if (!response.ok) throw new Error('Failed to fetch schedule data');

                const data = await response.json();
                if (data && data.getSpecialistScheduleDetail && data.getSpecialistScheduleDetail.length > 0) {
                    const schedule = data.getSpecialistScheduleDetail[0];
                    setDoctorId(schedule.doctorId); // Store doctorId from the API response
                    setFormData({
                        startTime: schedule.startTime, // Example: "12:00"
                        endTime: schedule.endTime,     // Example: "02:00"
                        dayofweek: schedule.dayofweek,
                        status: schedule.status || 'Active', // Assuming default status is 'Active'
                    });
                    setScheduleData(data.getSpecialistScheduleDetail); // Save current schedule data
                }
            } catch (error) {
                console.error('Error fetching schedule data:', error);
                Swal.fire({
                    title: 'Error!',
                    text: error.message || 'Failed to load schedule data.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        };

        fetchScheduleData();
    }, [id]);

    // Fetch conflicting time ranges when the day of the week is selected
    useEffect(() => {
        const checkScheduleAvailability = async (day) => {
            const userId = id; // Assuming UserId is stored in sessionStorage

            if (!userId) {
                console.error("User not found in session.");
                return;
            }

            try {
                const response = await fetch(`${BASE_URL}SpecialistDoctorCheckSchedule?Userid=${doctorId}&Day=${day}`);

                if (!response.ok) {
                    throw new Error("Failed to fetch schedule availability.");
                }

                const data = await response.json();
                console.log('Schedule availability data:', data);

                // Find all conflicting times
                const conflictingTimeRanges = data.getSpecialistScheduleCheck?.map((schedule) => ({
                    startTime: schedule.startTime,
                    endTime: schedule.endTime
                })) || [];

                setConflictingTimes(conflictingTimeRanges); // Store conflicting times
            } catch (error) {
                console.error("Error fetching schedule availability:", error);
            }
        };

        // Trigger the availability check when the day of the week changes
        if (formData.dayofweek && formData.dayofweek !== 'Select Day') {
            checkScheduleAvailability(formData.dayofweek);
        } else {
            setConflictingTimes([]); // Reset conflicting times when day is not selected
        }
    }, [formData.dayofweek, doctorId, id]);

    // Function to check if a time is part of the current schedule
    const isHighlighted = (time) => {
        return scheduleData.some((schedule) =>
            schedule.startTime === time.startTime && schedule.endTime === time.endTime
        );
    };

    // Handle form input changes
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // Handle time changes (start and end time)
    const handleTimeChange = (name, hour, minute) => {
        const formattedTime = `${hour}:${minute}`;
        setFormData((prevData) => ({
            ...prevData,
            [name]: formattedTime,
        }));
    };

    // Handle form submission to update schedule
    const handleSubmit = async (event) => {
        event.preventDefault();

        const { startTime, endTime, dayofweek, status } = formData;

        const requestPayload = {
            id: id,
            startTime: startTime,
            endTime: endTime,
            dayofWeek: dayofweek,
            status: status
        };

        setLoading(true); // Set loading state to true before submitting

        try {
            const response = await fetch(`${BASE_URL}SpecialistDoctorScheduleEdit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestPayload),
            });

            if (!response.ok) {
                const errorResponse = await response.json();
                throw new Error(errorResponse.message || 'Failed to update schedule');
            }

            const data = await response.json();
            console.log('Schedule updated successfully:', data);

            Swal.fire({
                title: 'Success!',
                text: 'Schedule updated successfully!',
                icon: 'success',
                confirmButtonText: 'OK'
            }).then(() => {
                // Navigate to DoctorSchedule/doctorId after success
                navigate(`/DoctorSchedule/${doctorId}`);
            });

        } catch (error) {
            console.error('Failed to update schedule:', error);
            Swal.fire({
                title: 'Error!',
                text: error.message || 'An unexpected error occurred.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false); // Set loading state to false after request is complete
        }
    };

    const formatTo12Hour = (time24) => {
        const [hour, minute] = time24.split(':').map(Number);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12; // convert '0' to '12'
        return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
    };

    const timeToMinutes = (time) => {
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
    };


    return (
        <div className="container-fluid">
            <Header />
            <div className="row justify-content-center mt-5">
                <div className="col-12 col-md-6">
                    <div className="card shadow-sm">
                        <div className="card-header text-center bg_primary text-white">
                            <h5 className="fw-bold mb-1">Edit Doctor Schedule</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="dayofweek" className="fw-bold">Select Day</Label>
                                            <Input
                                                type="select"
                                                name="dayofweek"
                                                value={formData.dayofweek}
                                                onChange={handleInputChange}
                                            >
                                                {daysOfWeek.map((day, index) => (
                                                    <option key={index} value={day === 'Select Day' ? '' : day}>
                                                        {day}
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="startTime" className="fw-bold">Start Time</Label>
                                            <Input
                                                type="select"
                                                id="startTime"
                                                onChange={(e) => handleTimeChange('startTime', e.target.value.split(':')[0], e.target.value.split(':')[1])}
                                                value={formData.startTime}
                                            >
                                                {hoursOptions.map((hour) =>
                                                    minutesOptions.map((minute) => (
                                                        <option key={`${hour}-${minute}`} value={`${hour}:${minute}`}>
                                                            {`${hour}:${minute}`}
                                                        </option>
                                                    ))
                                                )}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="endTime" className="fw-bold">End Time</Label>
                                            <Input
                                                type="select"
                                                id="endTime"
                                                onChange={(e) =>
                                                    handleTimeChange('endTime', e.target.value.split(':')[0], e.target.value.split(':')[1])
                                                }
                                                value={formData.endTime}
                                            >
                                                {hoursOptions.map((hour) =>
                                                    minutesOptions.map((minute) => {
                                                        const endTime = `${hour}:${minute}`;
                                                        const startMinutes = timeToMinutes(formData.startTime || "00:00");
                                                        const endMinutes = timeToMinutes(endTime);

                                                        const isAfterStart = !formData.startTime || endMinutes > startMinutes;

                                                        return (
                                                            <option
                                                                key={`${hour}-${minute}`}
                                                                value={endTime}
                                                                disabled={!isAfterStart}
                                                                className={!isAfterStart ? "text-danger" : ""}
                                                            >
                                                                {endTime}
                                                            </option>
                                                        );
                                                    })
                                                )}
                                            </Input>
                                        </FormGroup>

                                    </Col>
                                </Row>

                                <Row className="mb-3">

                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="status" className="fw-bold">Status</Label>
                                            <Input
                                                type="select"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                            >
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    {/* Display only one table: Current Schedule or Conflicting Schedule */}
                                    {(scheduleData.length > 0 && !conflictingTimes.length) && (
                                        <div className="mt-4">
                                            <h6>Current Schedule</h6>
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>Start Time</th>
                                                        <th>End Time</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {scheduleData.map((schedule, index) => (
                                                        <tr key={index} style={isHighlighted(schedule) ? { backgroundColor: '#c8e6c9' } : {}}>
                                                            <td>{schedule.startTime}</td>
                                                            <td>{schedule.endTime}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {(conflictingTimes.length > 0 && formData.dayofweek && formData.dayofweek !== 'Select Day') && (
                                        <div className="mt-4">
                                            <h6>Schedule</h6>
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>Start Time</th>
                                                        <th>End Time</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {conflictingTimes.map((conflict, index) => (
                                                        <tr key={index} style={isHighlighted(conflict) ? { backgroundColor: '#c8e6c9' } : {}}>
                                                            <td>{formatTo12Hour(conflict.startTime)}</td>
                                                            <td>{formatTo12Hour(conflict.endTime)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                </Row>

                                <Button type="submit" color="primary" className='btn_primary' block disabled={loading}>
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                        'Update Schedule'
                                    )}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditDoctorSchedule;
